import React from 'react';
import './moodmate-visual.css';

/**
 * AnimatedProgressRing — an SVG ring whose arc fills with `value` (0–1).
 *
 * Used by the breathing guide and the focus timer so both share one circular
 * visual. Pure SVG stroke-dashoffset (cheap, GPU-friendly); the stroke eases to
 * each new value, and the transition is removed under prefers-reduced-motion
 * (moodmate-visual.css). Center content is passed as children.
 */
export interface AnimatedProgressRingProps {
  value: number; // 0..1
  size?: number; // px
  stroke?: number; // px
  children?: React.ReactNode;
  label?: string;
  /** Pause the smooth easing (e.g. an instant reset) by rendering without transition. */
  instant?: boolean;
  /** Override the fill transition duration (ms) — e.g. match a breathing phase. */
  durationMs?: number;
  /** Easing for the fill transition. */
  easing?: string;
}

export const AnimatedProgressRing: React.FC<AnimatedProgressRingProps> = ({
  value,
  size = 220,
  stroke = 14,
  children,
  label,
  instant = false,
  durationMs,
  easing = 'linear',
}) => {
  const clamped = Math.max(0, Math.min(1, value));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - clamped);
  const valueStyle: React.CSSProperties = instant
    ? { transition: 'none' }
    : durationMs != null
    ? { transition: `stroke-dashoffset ${durationMs}ms ${easing}` }
    : {};

  return (
    <div className="mm-ring" style={{ width: size, height: size }} role="img" aria-label={label}>
      <svg className="mm-ring__svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle className="mm-ring__track" cx={size / 2} cy={size / 2} r={radius} strokeWidth={stroke} />
        <circle
          className="mm-ring__value"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={valueStyle}
        />
      </svg>
      <div className="mm-ring__center">{children}</div>
    </div>
  );
};

export default AnimatedProgressRing;
